import * as React from 'react'
import { t } from 'ttag'
import { currentLang, isRtl } from '../../i18nInit'
import YumButton from '../YumButton/YumButton'
import { ReactComponent as GoogleIcon } from '../../assets/v2/icons/icon-google.svg'
import { isNative } from '../../common/helpers/isNative'
import { openUrl } from '../../common/helpers/routeHelpers'
import { ROUTES } from '../../common/Routes/routePaths'
import { formatIntegrationUrl } from '../../common/helpers/integrationUrl'
import { Card } from '../Card/Card'
import Loader from '../Loader/Loader'
import { Button } from '../Button/Button'
import { store } from '../../index'
import { IGlobalState } from '../../Root/store/appReducer'
import { appDomain } from '../../services/apiUrl'
import NotificationsWrapper from '../Notification/NotificationsWrapper'
import { push } from 'connected-react-router'
import { Dialog } from '@capacitor/dialog'
import { Preferences } from '@capacitor/preferences'

const GOOGLE_SIGN_IN_CLIENT_ID = '483324513425-6ici7buturg7l6iirkv7a2nls4inlvuv.apps.googleusercontent.com'

export interface IStateProps {
  buttonText?: string
  isSignUp: boolean
  disabled?: boolean
}

export interface IDispatchProps {
  doGoogleSignIn(payload: any): void
}

type YumGoogleSignInProps = IStateProps & IDispatchProps

const randomString = (length: number) => {
  const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._'
  let result = ''

  while (length > 0) {
    const bytes = new Uint8Array(16)
    const random = window.crypto.getRandomValues(bytes)

    // eslint-disable-next-line
    random.forEach(function (c) {
      if (length === 0) {
        return
      }
      if (c < charset.length) {
        result += charset[c]
        length--
      }
    })
  }
  return result
}

export const getGoogleSignInRedirectUri = () => {
  let redirectUri
  if (!isNative() && window.location.pathname) {
    redirectUri = `${appDomain}/web-proxy${window.location.pathname}`
  } else {
    // For Cordova apps, we're adding a proxy to know later if and where to redirect.
    const proxy = isNative() ? '/app-proxy' : '/web-proxy'
    const globalState = store.getState() as any as IGlobalState
    if (globalState.router && globalState.router.location && globalState.router.location.pathname) {
      redirectUri = `${appDomain}${proxy}${globalState.router.location.pathname}`
    } else {
      redirectUri = `${appDomain}${proxy}${ROUTES.SIGNUP.to}`
    }
  }
  return redirectUri
}

const getGoogleIntegrationUrl = async (optimizeForGoogleWorkspace: boolean) => {
  const baseUrl = 'https://accounts.google.com/o/oauth2/v2/auth'

  const redirectUri = getGoogleSignInRedirectUri()

  // Mitigate replay attacks when using the implicit flow.
  const nonce = randomString(16)
  await Preferences.set({ key: 'googleSignInNonce', value: nonce })

  const parameters = [
    'response_type=code',
    `nonce=${nonce}`,
    'scope=openid%20profile%20email',
    'prompt=consent',
    `client_id=${GOOGLE_SIGN_IN_CLIENT_ID}`,
    `redirect_uri=${redirectUri}`,
    // We need this to get refresh token.
    'access_type=offline',
  ]

  if (optimizeForGoogleWorkspace) {
    // Optimize the login for G Suite accounts.
    parameters.push('hd=*')
  }

  return formatIntegrationUrl(baseUrl, parameters)
}

const finalizeSignUp = async (signUpCode: string, isSignUp: boolean) => {
  const hasSignUpCode = signUpCode.trim() !== ''

  if (hasSignUpCode) {
    await Preferences.set({ key: 'googleSignUpCode', value: signUpCode.trim() })
  } else {
    await Preferences.remove({ key: 'googleSignUpCode' })
  }

  const url = await getGoogleIntegrationUrl(!hasSignUpCode && isSignUp)
  await openUrl(url)
}

const signUpWithGoogle = (isSignUp: boolean) => async () => {
  let signUpCode: string | null = ''

  if (isSignUp) {
    const { value: consent } = await Dialog.confirm({
      title: '',
      message: t`Do you agree to YuMuuv's Terms of Service and Privacy Policy?`,
      okButtonTitle: t`Yes, I agree!`,
      cancelButtonTitle: t`No`,
    })
    if (!consent) return

    const { value: hasGoogleWorkplaceIntegration } = await Dialog.confirm({
      title: `Google Workspace`,
      message: t`Does your company have Google Workspace integration with YuMuuv?`,
      okButtonTitle: t`Yes`,
      cancelButtonTitle: t`No`,
    })

    if (!hasGoogleWorkplaceIntegration) {
      const { value, cancelled } = await Dialog.prompt({
        title: '',
        message: t`Sign-up code`,
        okButtonTitle: t`OK`,
        cancelButtonTitle: t`Cancel`,
      })
      signUpCode = value
      if (!signUpCode || cancelled) return
    }
  }

  await finalizeSignUp(signUpCode, isSignUp)
}

export class YumGoogleSignIn extends React.PureComponent<YumGoogleSignInProps> {
  async componentDidMount(): Promise<void> {
    if (window.location.search) {
      const { doGoogleSignIn } = this.props
      const searchParams = new URLSearchParams(window.location.search)
      const authorizationCode = searchParams.get('code') || ''
      const redirectUri = getGoogleSignInRedirectUri()
      const { value: nonce } = await Preferences.get({ key: 'googleSignInNonce' })
      const { value: signUpCode } = await Preferences.get({ key: 'googleSignUpCode' })

      if (authorizationCode) {
        doGoogleSignIn({
          authorizationCode,
          redirectUri,
          nonce: nonce || '',
          lang: currentLang,
          redirectTo: window.location.pathname,
          signUpCode: signUpCode || '',
        })
      }
    }
  }

  render() {
    return (
      <YumButton
        onClick={signUpWithGoogle(this.props.isSignUp)}
        variant="outlined"
        style={{
          width: '100%',
          whiteSpace: 'nowrap',
          fontWeight: 400,
          /*paddingLeft: 18,
          paddingRight: 18,
          marginBottom: 16,
          marginLeft: 0,
          marginRight: 0,
          lineHeight: '34px',
          fontSize: '14px',*/
          marginBottom: '16px',
          fontSize: '14px',
          //display: 'inline-block',
          //textTransform: 'unset',
        }}
        disabled={this.props.disabled}
      >
        <>
          <GoogleIcon style={{ marginTop: -2, marginRight: isRtl ? 0 : 10, marginLeft: isRtl ? 10 : 0 }} />
          {this.props.buttonText || t`Sign up with Google`}
        </>
      </YumButton>
    )
  }
}

export class AppProxy extends React.Component<IDispatchProps> {
  componentDidMount(): void {
    const searchParams = new URLSearchParams(window.location.search)
    const authorizationCode = searchParams.get('code')
    const signInWithApple = searchParams.get('signInWithApple')

    const sessionId = searchParams.get('sessionId')
    const error = searchParams.get('error')

    if (authorizationCode || signInWithApple || sessionId || error) {
      const path = window.location.pathname.slice(ROUTES.APP_PROXY.to.length - 1)
      // TODO: Don't do this for web...
      const url = `yumuuv://${path}${window.location.search}`
      console.log(`Redirect to: ${url}`)
      openUrl(url)
    } else {
      store.dispatch(push('/'))
    }
  }

  render() {
    return (
      <Card className="card--center-content pt-12">
        <NotificationsWrapper />
        <Loader />

        <p>{t`Please wait, integration with third party in progress`}</p>

        <div>
          <Button text={t`Cancel`} url={ROUTES.LANDING.to} link={true} />
        </div>
      </Card>
    )
  }
}

export class WebProxy extends React.Component<IDispatchProps> {
  componentDidMount(): void {
    /*const searchParams = new URLSearchParams(window.location.search)
    const authorizationCode = searchParams.get('code')
    const signInWithApple = searchParams.get('signInWithApple')*/

    const path = window.location.pathname.slice(ROUTES.APP_PROXY.to.length - 1)
    // TODO: Don't do this for web...
    const url = `/${path}${window.location.search}`
    console.log('WebProxy | redirect to:', url)
    store.dispatch(push(url))

    /*    if (authorizationCode || signInWithApple) {
      const path = window.location.pathname.slice(ROUTES.APP_PROXY.to.length - 1)
      /!*const url = `yumuuv://${path}${window.location.search}`
      console.log(`Redirect to: ${url}`)
      openUrl(url)*!/
    } else {
      store.dispatch(push('/'))
    }*/
  }

  render() {
    return (
      <Card className="card--center-content pt-12">
        <NotificationsWrapper />
        <Loader />

        <p>{t`Please wait, integration with third party in progress`}</p>

        <div>
          <Button text={t`Cancel`} url={ROUTES.LANDING.to} link={true} />
        </div>
      </Card>
    )
  }
}
